import { useState, useCallback, useEffect, useRef } from 'react'

const DEFAULT_ANIMATION_DURATION = 150

interface UsePopupOptions {
  isOpen: boolean
  onClose: () => void
  animationDuration?: number
}

interface UsePopupReturn {
  /** Нужно ли рендерить контент (true пока идёт анимация закрытия) */
  shouldRender: boolean
  /** Текущее состояние для data-state атрибута */
  dataState: 'open' | 'closing'
  /** Обработчик закрытия с анимацией */
  handleClose: () => void
}

/**
 * Хук для управления попапами/модалками с анимацией закрытия.
 * 
 * Решает проблему:
 * - Компонент должен оставаться в DOM пока проигрывается анимация закрытия
 * - После анимации вызывается onClose и компонент размонтируется
 * 
 * @example
 * ```tsx
 * const { shouldRender, dataState, handleClose } = usePopup({ isOpen, onClose })
 * 
 * if (!shouldRender) return null
 * 
 * return (
 *   <div data-state={dataState} onClick={handleClose}>
 *     ...
 *   </div>
 * )
 * ```
 */
export function usePopup({
  isOpen,
  onClose,
  animationDuration = DEFAULT_ANIMATION_DURATION,
}: UsePopupOptions): UsePopupReturn {
  const [isClosing, setIsClosing] = useState(false)
  const closeTimerRef = useRef<number | null>(null)

  // Очищаем таймер при размонтировании
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  const handleClose = useCallback(() => {
    // Предотвращаем повторный вызов во время закрытия
    if (isClosing) return

    setIsClosing(true)
    closeTimerRef.current = window.setTimeout(() => {
      setIsClosing(false)
      onClose()
      closeTimerRef.current = null
    }, animationDuration)
  }, [isClosing, onClose, animationDuration])

  // Компонент рендерится пока открыт ИЛИ пока идёт анимация закрытия
  const shouldRender = isOpen || isClosing
  const dataState = isClosing ? 'closing' : 'open'

  return {
    shouldRender,
    dataState,
    handleClose,
  }
}
